import React from 'react';

import { widgetsConfigurations } from 'config/widgetConfigurations/widgetsConfigurations';
import { Link } from 'react-router-dom';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';
import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseWidget } from 'hooks/useBaseWidget';

import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { MuiIcon } from 'components/common/Icons/base/MuiIcon/MuiIcon';
import { List } from 'components/common/ListWrapper/list';
import NoData from 'components/common/NoData/noData';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetExistInBackend } from 'components/common/WidgetExistInBackend/WidgetExistInBackend';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';

import {
    IComponentProps,
    ILocalFilters,
} from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { executionConfig } from 'components/Dashboards/Project/ProjectWrapper/executionConfig';
import { AddToReport } from 'components/Dashboards/Reporting/AddToReport/AddToReport';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { Narrative } from 'components/Dashboards/Reporting/WidgetNarrativeWrapper/Narrative';
import { PinboardPills } from 'components/Dashboards/Reporting/pinboardPills/PinboardPills';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { Button, CustomizedTooltip } from 'components/common';
import { usePrioritiesMatrixComponentProvider } from 'components/widgets/PrioritiesMatrix/PrioritiesMatrix.provider';
import { Cards } from 'components/widgets/PrioritiesMatrix/components/Cards/Cards';
import { Matrix } from 'components/widgets/PrioritiesMatrix/components/Matrix/Matrix';
import { usePrioritiesMatrixLogic } from 'components/widgets/PrioritiesMatrix/prioritiesMatrix.logic';
import classes from 'components/widgets/PrioritiesMatrix/prioritiesMatrix.module.scss';
import { PRIORITIES_MATRIX_FILTERS } from 'components/widgets/PrioritiesMatrix/prioritiesMatrix.utils';

export interface LocalFilters extends ILocalFilters {
    tagsQuery: { tags: null | string; excludeTags: null | string } | null;
}

export interface Props extends IComponentProps {
    localFilters: LocalFilters;
}

const { componentKey, id, endpoint } = widgetsConfigurations.prioritiesMatrix;

export const PrioritiesMatrix = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    globalFilterData,
    externalComponents = null,
    editNarrative = null,
    localFilters,
    setSubTitleForExport,
    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title = widgetsConfigurations.prioritiesMatrix.title,
}: Props) => {
    const { ui, api } = usePrioritiesMatrixComponentProvider({
        widgetId,
        contractId,
        projectId,
        latestVersionId,
        globalFilterData,
        localFilters,
    });

    const { logic } = usePrioritiesMatrixLogic({
        ui,
        api,
        widgetId,
    });

    const { subTitle, route, editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
        ui,
        reportWidgetRef,
        apiPayload: api.matrixData.payload,
        widgetEndpoint: endpoint,
    });

    return (
        <WidgetExistInBackend componentKey={componentKey}>
            <WidgetWithTitle
                title={
                    <WidgetTitle title={editableTitle} setTitle={setEditableTitle} widgetViewMode={widgetViewMode} />
                }
                className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
                id={id}
                titleComponents={[
                    !widgetId && (
                        <div className={classes.filters} key={'ListWrapper'}>
                            <div className={classes.filtersWrapper}>
                                <div className={classes.filterTitle}>{`${logic.filtersTitle}:`}</div>
                                <List<string>
                                    onChange={logic.handleFilterClick}
                                    options={PRIORITIES_MATRIX_FILTERS}
                                    value={logic.urgentFilter}
                                />
                            </div>
                        </div>
                    ),
                    externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                    <Link
                        to={AppRoutes.buildPath(
                            AppRoutes.DASHBOARD_PLANNING_PATH,
                            executionConfig.programLibrary.link,
                            executionConfig.subProgramPreferences.link,
                        )}
                    >
                        <CustomizedTooltip
                            triggerElement={
                                <Button variant={'tertiary'} size={'small'}>
                                    <MuiIcon fontSize={'2.5rem'} icon={'tune'} color={'var(--button-tertiary-fg)'} />
                                </Button>
                            }
                            tooltipContent={'Sub Program Preferences'}
                        />
                    </Link>,
                    <AddToReport
                        key={'addWidgetToReport'}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        projectId={projectId}
                        contractId={contractId}
                        uiState={ui.state}
                        shouldHide={WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode)}
                        apiPayload={api.matrixData.payload}
                        widgetEndpoint={endpoint}
                    />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ExportDropdown key={'export'} title={title} subTitle={subTitle} />
                    ),
                    <TooltipIcon key={'info'} tooltip={api.matrixData.instance.data?.info} />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ThreeDotsMenu
                            widgetViewMode={widgetViewMode}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            shareTitle={title}
                            shareLink={route}
                        />
                    ),
                ]}
                titleFilters={[
                    <ReportingVersionAndPills
                        key={'ReportingVersionAndPills'}
                        selectedProjectAndContract={widgetSelectedProjectAndContract}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                    <Narrative key={'narrative'} editNarrative={editNarrative} />,
                    <PinboardPills
                        key={'pinboardPills'}
                        isInPinboard={WidgetViewModeUtils.isPinboardView(widgetViewMode)}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                ]}
            >
                <div className={classes.prioritiesMatrix}>
                    <OverlayWithSpinner isLoading={api.cards.isLoading && !api.matrixData.instance.data} />
                    {!api.cards.isLoading && !api.matrixData.instance.data && <NoData />}
                    {api.matrixData.instance.data && (
                        <>
                            <Matrix
                                buttons={api.matrixData.instance.data}
                                handleMatrixButtonClick={logic.handleMatrixButtonClick}
                                selected={ui.state.selectedTypeAndRole}
                            />
                            <Cards
                                selected={ui.state.selectedTypeAndRole}
                                linkToBattlecards={logic.linkToProjectActivities}
                                buttons={api.matrixData.instance.data}
                                selectedTypeWithRole={logic.selectedTypeWithRole}
                                isOverdueFilter={ui.state.isOverdueFilter}
                                handleOverdueFilter={() => ui.setters.setIsOverdueFilter(!ui.state.isOverdueFilter)}
                                isDelayDriverFilter={ui.state.isDelayDriverFilter}
                                handleDelayDriverFilter={() => {
                                    ui.setters.setIsDelayDriverFilter(!ui.state.isDelayDriverFilter);
                                }}
                                isCriticalPathFilter={ui.state.isCriticalPathFilter}
                                handleCriticalPathFilter={() => {
                                    ui.setters.setIsCriticalPathFilter(!ui.state.isCriticalPathFilter);
                                }}
                                isAssignedToMeFilter={ui.state.isAssignedToMeFilter}
                                handleAssignedToMeFilter={() => {
                                    ui.setters.setIsAssignedToMeFilter(!ui.state.isAssignedToMeFilter);
                                }}
                                isCardsLoading={api.cards.isLoading}
                                filteredCardsData={logic.filteredCardsData}
                                cards={api.cards.data}
                            />
                        </>
                    )}
                </div>
            </WidgetWithTitle>
        </WidgetExistInBackend>
    );
};
